'use client';

import { VStack } from '@terra/ui-styled-system';
import {
  Key01Icon,
  Mail01Icon,
  SmartPhone01Icon,
  WebhookIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { MfaProviderSchema } from '@terra/shared-schemas-orchestrator';
import { Switch } from '../../components/switch';
import { FieldWrapper } from '../../components/field-wrapper';
import { BodyDefault } from '../../components/typography';
import type {
  MfaCredentialBinding,
  MfaFetchMessagesFn,
  MfaFetchWebhookConfigFn,
  MfaProvider,
  MfaProviderOption,
  MfaTotpSecretRevealProps,
} from './mfa.types';
import { MfaProviderRowPanel } from './MfaProviderRowPanel';

export type { MfaProvider } from './mfa.types';
type MfaConfigurationProps = MfaCredentialBinding & {
  mfaProviders: MfaProvider[];
  onMfaProvidersChange: (providers: MfaProvider[]) => void;
  onFetchMfaMessages?: MfaFetchMessagesFn;
  onFetchMfaWebhookConfig?: MfaFetchWebhookConfigFn;
  totpSecretReveal?: MfaTotpSecretRevealProps;
};
const MFA_PROVIDER_DEFINITIONS = [
  {
    value: MfaProviderSchema.enum.EMAIL,
    labelKey: 'providerOptions.email.label',
    descriptionKey: 'providerOptions.email.description',
    icon: Mail01Icon,
  },
  {
    value: MfaProviderSchema.enum.SMS,
    labelKey: 'providerOptions.sms.label',
    descriptionKey: 'providerOptions.sms.description',
    icon: SmartPhone01Icon,
  },
  {
    value: MfaProviderSchema.enum.OTP,
    labelKey: 'providerOptions.otp.label',
    descriptionKey: 'providerOptions.otp.description',
    icon: Key01Icon,
  },
  {
    value: MfaProviderSchema.enum.WEBHOOK,
    labelKey: 'providerOptions.webhook.label',
    descriptionKey: 'providerOptions.webhook.description',
    icon: WebhookIcon,
  },
] as const;

export function MfaConfiguration({
  mfaProviders,
  credentialId,
  applicationId,
  onMfaProvidersChange,
  onFetchMfaMessages,
  onFetchMfaWebhookConfig,
  totpSecretReveal,
}: MfaConfigurationProps) {
  const t = useTranslations('designSystem.mfa');
  const tCommon = useTranslations('designSystem.common');
  const providerOptions: MfaProviderOption[] = MFA_PROVIDER_DEFINITIONS.map(
    (definition) => ({
      value: definition.value,
      label: t(definition.labelKey),
      description: t(definition.descriptionKey),
      icon: definition.icon,
    }),
  );
  const findRow = (
    providerType: MfaProviderOption['value'],
  ): MfaProvider | null =>
    mfaProviders.find((row) => row.providerType === providerType) ?? null;
  const masterEnabled = mfaProviders.some((row) => row.enabled);
  const mfaTypeLabel = providerOptions
    .filter((option) =>
      mfaProviders.some(
        (row) => row.providerType === option.value && row.enabled,
      ),
    )
    .map((option) => option.label)
    .join(', ');
  const handleGlobalToggle = (checked: boolean) => {
    if (!checked) {
      onMfaProvidersChange(
        mfaProviders.map((row) => ({ ...row, enabled: false })),
      );

      return;
    }

    const emailRow = findRow(MfaProviderSchema.enum.EMAIL);

    onMfaProvidersChange([
      ...mfaProviders.filter(
        (row) => row.providerType !== MfaProviderSchema.enum.EMAIL,
      ),
      emailRow
        ? { ...emailRow, enabled: true }
        : {
            enabled: true,
            providerType: MfaProviderSchema.enum.EMAIL,
          },
    ]);
  };

  const handleProviderToggle = (
    providerType: MfaProviderOption['value'],
    enabled: boolean,
  ) => {
    const existing = findRow(providerType);

    if (existing) {
      onMfaProvidersChange(
        mfaProviders.map((row) =>
          row.providerType === providerType ? { ...row, enabled } : row,
        ),
      );
    } else {
      onMfaProvidersChange([
        ...mfaProviders,
        { enabled, providerType } as MfaProvider,
      ]);
    }
  };

  const handleTotpSecretChange = (secret: string) => {
    const existing = findRow(MfaProviderSchema.enum.OTP);

    if (existing) {
      onMfaProvidersChange(
        mfaProviders.map((row) =>
          row.providerType === MfaProviderSchema.enum.OTP
            ? { ...row, totpSecret: secret || undefined }
            : row,
        ),
      );
    } else {
      onMfaProvidersChange([
        ...mfaProviders,
        {
          enabled: true,
          providerType: MfaProviderSchema.enum.OTP,
          totpSecret: secret || undefined,
        },
      ]);
    }
  };

  return (
    <VStack alignItems="stretch" gap={4}>
      <FieldWrapper label={t('label')}>
        <Switch
          size="sm"
          checked={masterEnabled}
          onCheckedChange={(details) => handleGlobalToggle(details.checked)}
        >
          <BodyDefault as="span" color="fg.muted">
            {masterEnabled ? tCommon('enabled') : tCommon('disabled')}
          </BodyDefault>
        </Switch>
      </FieldWrapper>

      {masterEnabled ? (
        <VStack alignItems="stretch" gap={2}>
          <FieldWrapper label={t('mfaType')}>
            <BodyDefault color="fg.default">{mfaTypeLabel || '-'}</BodyDefault>
          </FieldWrapper>
          {providerOptions.map((option) => (
            <MfaProviderRowPanel
              key={option.value}
              providerOption={option}
              mfaProvider={findRow(option.value)}
              onToggle={(enabled) =>
                handleProviderToggle(option.value, enabled)
              }
              onTotpSecretChange={
                option.value === MfaProviderSchema.enum.OTP
                  ? handleTotpSecretChange
                  : undefined
              }
              credentialId={credentialId}
              applicationId={applicationId}
              onFetchMfaMessages={onFetchMfaMessages}
              onFetchMfaWebhookConfig={onFetchMfaWebhookConfig}
              totpSecretReveal={
                option.value === MfaProviderSchema.enum.OTP
                  ? totpSecretReveal
                  : undefined
              }
            />
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
}

export type { MfaConfigurationProps };
